/* eslint-disable lingui/no-unlocalized-strings */
/* eslint-disable lingui/text-restrictions */
import type { Appearance } from "@clerk/types";

/**
 * theResume-branded appearance for all Clerk components (SignIn, SignUp, UserButton).
 * Committed sky-blue, Inter, rounded-full CTAs, zinc neutrals.
 *
 * Height/border/overflow for inputs and OTP are enforced in styles/_clerk.css because
 * Clerk's internal styles win over Tailwind utilities on those elements.
 */
export const clerkAppearance: Appearance = {
  variables: {
    colorPrimary: "#0284c7", // sky-600, accessible on light surfaces
    colorText: "#18181b", // zinc-900
    colorTextSecondary: "#52525b", // zinc-600
    colorBackground: "transparent",
    colorInputBackground: "#fbfdff",
    colorInputText: "#18181b",
    colorDanger: "#e11d48", // rose-600
    colorSuccess: "#059669", // emerald-600
    colorNeutral: "#71717a", // zinc-500
    borderRadius: "0.75rem",
    fontFamily: '"Inter", ui-sans-serif, system-ui, sans-serif',
    fontSize: "0.9375rem",
    spacingUnit: "1rem",
  },
  layout: {
    socialButtonsPlacement: "top",
    socialButtonsVariant: "blockButton",
    logoPlacement: "none",
    showOptionalFields: true,
    animations: true,
  },
  elements: {
    rootBox: "w-full !overflow-visible",
    cardBox: "w-full !bg-transparent !shadow-none !border-0 !rounded-none !overflow-visible",
    card: "bg-transparent shadow-none border-0 w-full p-0 gap-5 !overflow-visible",
    header: "hidden",
    main: "gap-5 !overflow-visible",
    form: "!overflow-visible",
    socialButtons: "gap-3",
    socialButtonsBlockButton:
      "h-12 rounded-xl border border-slate-200 bg-[#fbfdff] text-slate-800 font-medium hover:bg-sky-50/50 hover:border-sky-200 transition-colors duration-200 shadow-none",
    socialButtonsBlockButtonText: "font-medium text-[0.9375rem]",
    dividerRow: "my-5",
    dividerLine: "bg-slate-200",
    dividerText: "text-slate-500 text-[0.6875rem] font-medium uppercase tracking-[0.14em]",
    formFieldLabel: "text-slate-700 font-medium text-sm mb-1.5",
    formFieldLabelRow: "mb-0",
    formFieldInput:
      "h-12 rounded-xl border border-slate-200 bg-[#fbfdff] text-slate-900 placeholder:text-slate-500",
    formButtonPrimary:
      "h-12 rounded-full bg-sky-600 hover:bg-sky-700 active:bg-sky-800 text-white font-semibold text-[0.9375rem] normal-case tracking-normal transition-colors duration-200 after:hidden",
    formButtonReset: "text-zinc-500 hover:text-zinc-700 rounded-full",
    otpCodeField: "w-full !overflow-visible",
    otpCodeFieldInputs: "w-full gap-2 !overflow-visible justify-between",
    otpCodeFieldInput:
      "rounded-xl border border-slate-200 bg-[#fbfdff] text-slate-900 font-semibold text-lg",
    formResendCodeLink: "text-sky-600 hover:text-sky-700 font-medium",
    identityPreview: "rounded-xl border border-slate-200 bg-sky-50/50",
    identityPreviewEditButton: "text-sky-600 hover:text-sky-700",
    formFieldAction: "text-sky-600 hover:text-sky-700 font-medium",
    formFieldInputShowPasswordButton: "text-zinc-400 hover:text-zinc-600",
    footer: "mt-2 bg-transparent border-0 [&>div]:bg-transparent [&>div]:border-0",
    footerAction: "bg-transparent",
    footerActionText: "text-zinc-500 text-sm",
    footerActionLink: "text-sky-600 hover:text-sky-700 font-semibold",
    footerPagesLink: "text-zinc-400 hover:text-zinc-600",
    badge: "bg-sky-50 text-sky-700 rounded-full",
    formFieldSuccessText: "text-emerald-600",
    formFieldErrorText: "text-rose-600",
    alert: "rounded-xl border border-zinc-200 bg-zinc-50",
    spinner: "text-sky-500",
  },
};
